import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X, Save, Tag, ExternalLink, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PromoCodeManagement = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ code: '', redirect_url: '' });

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/promo-codes`, { headers });
      setCodes(response.data);
    } catch (error) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim() || !formData.redirect_url.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      await axios.post(`${API}/admin/promo-codes`, {
        code: formData.code.trim(),
        redirect_url: formData.redirect_url.trim(),
      }, { headers });
      toast.success('Promo code created');
      setShowForm(false);
      setFormData({ code: '', redirect_url: '' });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promoId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, { headers });
      toast.success('Promo code deleted');
      setDeleteConfirm(null);
      fetchCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[#222]">Promo Codes</h2>
          <p className="text-sm text-[#666]">{codes.length} active code{codes.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center gap-2"
          data-testid="add-promo-btn"
        >
          <Plus className="w-4 h-4" />
          Add Promo Code
        </Button>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <strong>How it works:</strong> When a customer enters a valid promo code at checkout, 
        the box becomes free and they are redirected to the URL you specify here instead of the Shopify checkout.
      </div>

      {/* Add Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-[#222]">Add Promo Code</h3>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#666] mb-1">Discount Code *</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. CAERPHILLY"
                    required
                    className="uppercase"
                    data-testid="promo-code-input"
                  />
                  <p className="text-xs text-[#999] mt-1">This is the code customers will enter at checkout</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#666] mb-1">Redirect URL *</label>
                  <Input
                    value={formData.redirect_url}
                    onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                    placeholder="https://ultimatepetfoods.co.uk/free-box-confirmation"
                    required
                    data-testid="promo-url-input"
                  />
                  <p className="text-xs text-[#999] mt-1">Customer will be sent here instead of Shopify checkout</p>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center justify-center gap-2"
                    data-testid="save-promo-btn"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Create
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#222]">Delete Promo Code?</h3>
                  <p className="text-sm text-[#666]">This code will no longer work at checkout.</p>
                </div>
              </div>
              <p className="text-sm text-[#666] mb-6">
                Delete code <strong className="font-mono">{deleteConfirm.code}</strong>?
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-promo-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Codes Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {codes.length === 0 ? (
          <div className="text-center py-12 text-[#666]">
            <Tag className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No promo codes yet</p>
            <p className="text-sm">Add a code to allow free box claims</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase">Redirect URL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#666] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {codes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#222] bg-gray-100 px-3 py-1 rounded-lg">
                          {code.code}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(code.code);
                            toast.success('Code copied!');
                          }}
                          className="p-1 hover:bg-gray-100 rounded text-[#666] hover:text-[#0FA49C] transition-colors"
                          title="Copy code"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={code.redirect_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 max-w-xs truncate"
                      >
                        {code.redirect_url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#666]">
                      {new Date(code.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end">
                        <button
                          onClick={() => setDeleteConfirm(code)}
                          className="p-2 hover:bg-red-50 rounded-lg text-[#666] hover:text-red-600 transition-colors"
                          title="Delete"
                          data-testid={`delete-promo-${code.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
